import {
	Dispatch,
	ReactNode,
	SetStateAction,
	useEffect,
	useState,
} from 'react';
import clsx from 'clsx';
import {
	FieldError,
	FieldErrorsImpl,
	FieldValues,
	Merge,
	UseFormRegister,
	UseFormSetValue,
} from 'react-hook-form';
import { Icon, Typography } from '@/components/common';
import { InputEmail } from '@/components/form';
import { CircleCheck, Plus } from 'lucide-react';
import { ModalVerification } from './ModalVerification';
import { verificationsService } from '@/api/verifications';
import { useNotification, useUser } from '@/hooks';
import { ErrorHttpResponse, RoleType } from '@/interfaces';
import Icons from '@/const/icons';
import { formatErrorMessage } from '@/lib';
import { authService } from '@/api';

interface Props {
	role: RoleType;
	error?: FieldError | Merge<FieldError, FieldErrorsImpl<any>> | undefined;
	register: UseFormRegister<FieldValues>;
	setValue: UseFormSetValue<FieldValues>;
	additionalEmail: string | undefined;
	addSecondEmail: boolean;
	isSecondEmailVerified: boolean | undefined;
	initialAdditionalEmail: string | null;
	refetch: () => void;
	setAddSecondEmail: Dispatch<SetStateAction<boolean>>;
}

export const FormInputEmails: React.FC<Props> = ({
	role,
	additionalEmail,
	initialAdditionalEmail,
	isSecondEmailVerified,
	addSecondEmail,
	register,
	refetch,
	error,
	setValue,
	setAddSecondEmail,
}) => {
	const { user } = useUser();
	const { toastSuccess, toastError } = useNotification();
	const [isClickedVerify, setIsClickedVerify] = useState(false);
	const [isModalOpen, setIsModalOpen] = useState(false);
	const [errorVerification, setErrorVerification] = useState<
		string | ReactNode
	>('');

	const isVerified = user?.is_verified;

	const MESSAGE_VERIFICATION_ADDITIONAL_EMAIL =
		"We've sent you an email to verify your additional email address.";
	const MESSAGE_VERIFICATION =
		"We've sent you an email to verify your email address.";

	useEffect(() => {
		setIsClickedVerify(false);
	}, [additionalEmail]);

	const handleClickVerifyEmail = (): void => {
		setIsClickedVerify(true);
		sentCodeAgain();
	};

	const sentCodeAgain = async (): Promise<void> => {
		try {
			if (!isVerified) {
				const email = user?.email;
				email && (await authService.resendVerificationEmail(email));
				toastSuccess(MESSAGE_VERIFICATION);
			} else {
				await verificationsService.resendEmailVerification();
				setIsModalOpen(true);
				toastSuccess(MESSAGE_VERIFICATION_ADDITIONAL_EMAIL);
			}
		} catch (error) {
			toastError(formatErrorMessage(undefined));
		}
	};

	const validateOtpCode = async (code: string): Promise<void> => {
		try {
			await verificationsService.validateEmailOtp(code);
			toastSuccess('Your email has been successfully verified.');

			refetch();
			setIsModalOpen(false);
		} catch (error) {
			const status = (error as ErrorHttpResponse).statusCode;
			if (!status) {
				toastError(formatErrorMessage(undefined));
				setIsModalOpen(false);
			} else if (status === 400) {
				setErrorVerification(
					<span>
						The verification code has expired. <br />
						Please request a new one and try again.
					</span>
				);
			} else {
				setErrorVerification(
					'The verification code is incorrect. Please try again.'
				);
			}
		}
	};

	const handleDeleteSecondEmail = (): void => {
		setAddSecondEmail(false);
		setValue('additionalEmail', '');
		toastSuccess(
			'Secondary email deleted, remember to save to complete changes.'
		);
	};
	return (
		<>
			<div className="flex flex-col gap-2">
				<div className="relative w-full md:w-[500px]">
					{role === 'client' && (
						<Typography
							type="regular-12"
							className={clsx('absolute opacity-60 top-6 left-5', {
								hidden: !addSecondEmail,
							})}
						>
							Primary
						</Typography>
					)}
					<InputEmail
						readOnly
						name="email"
						register={register}
						placeholder="lila@vans@gmail.com"
						className="w-full md:w-[500px]"
						classNameInput={clsx('pr-10 md:pr-0 !mt-0', {
							'pl-[4.5rem] md:pl-24': addSecondEmail && role === 'client',
							'!border-main-red !bg-bg-white': !isVerified,
							'!bg-transparent': isVerified,
						})}
						setValueCustom={setValue}
					/>
					{isVerified && (
						<div className="flex items-center gap-1 absolute top-4 right-5">
							<Typography
								type="regular-12"
								className="gap-1 text-green-130 hidden md:block"
							>
								Verified
							</Typography>
							<CircleCheck className="w-4 h-4 text-green-130" />
						</div>
					)}

					{!isVerified && (
						<div className="mt-2 ml-[18px] space-y-2">
							<div className="flex items-center gap-x-4">
								<Icon src={Icons.danger} width={16} height={16} />
								<Typography type="semibold-16" className="text-main-red">
									Verify your email
								</Typography>
							</div>
							<div className="flex items-center gap-x-1">
								<Typography type="regular-14" className="">
									{`We've sent an email. Didn't receive it? `}
								</Typography>
								<button
									type="button"
									className="text-purple-150 !text-base font-semibold"
									onClick={sentCodeAgain}
								>
									Resend
								</button>
							</div>
						</div>
					)}
				</div>
				<div
					className={clsx({
						hidden: addSecondEmail || !isVerified,
					})}
				>
					{role === 'client' && (
						<button
							className="flex pl-2 items-center gap-2 text-purple-150"
							type="button"
							onClick={() => setAddSecondEmail(true)}
						>
							<Plus />
							<Typography
								type="semibold-16"
								text="Add on additional email"
								className="text-purple-150 !text-sm lg:!text-base !font-medium lg:!font-semibold"
							/>
						</button>
					)}
				</div>
				{addSecondEmail && role === 'client' && (
					<div className="relative w-full md:w-[532px]">
						<Typography
							type="regular-12"
							className={'absolute opacity-60 top-6 left-5 z-10'}
						>
							Secondary
						</Typography>
						<div>
							<div className="flex items-center gap-x-4">
								<InputEmail
									name="additionalEmail"
									register={register}
									className="w-full md:w-[500px]"
									classNameInput="pl-24"
									error={error}
									setValueCustom={setValue}
								/>{' '}
								<Icon
									src={Icons.trash}
									width={16}
									height={30}
									className={clsx('min-w-[16px] text-main-red cursor-pointer')}
									onClick={handleDeleteSecondEmail}
								/>
							</div>
							<div
								className={clsx(
									'flex items-center gap-1 absolute top-6 right-[52px] ',
									{
										hidden: !isSecondEmailVerified,
									}
								)}
							>
								<Typography
									type="regular-12"
									className={clsx(' text-green-130  hidden md:block')}
								>
									Verified
								</Typography>
								<CircleCheck className="w-4 h-4 text-green-130" />
							</div>

							{isClickedVerify &&
								additionalEmail &&
								additionalEmail === initialAdditionalEmail &&
								!isSecondEmailVerified && (
									<div className="px-5 py-2">
										<Typography type="regular-14" className="">
											{`We've sent a code to your email. Didn't receive it? `}
											<button
												type="button"
												className="text-purple-150 !text-base font-semibold"
												onClick={sentCodeAgain}
											>
												Resend
											</button>
										</Typography>
									</div>
								)}
							<button
								type="button"
								className={clsx('px-5 py-2', {
									hidden:
										!additionalEmail ||
										additionalEmail !== initialAdditionalEmail ||
										isClickedVerify ||
										isSecondEmailVerified,
								})}
								onClick={handleClickVerifyEmail}
							>
								<Typography
									type="semibold-16"
									text="Verify your email"
									className="text-purple-150"
								/>
							</button>
						</div>
					</div>
				)}
			</div>

			<ModalVerification
				title="Verify your email"
				description="Enter the four-digit code sent to your email."
				isOpen={isModalOpen}
				setIsOpen={setIsModalOpen}
				setIsModalOpen={setIsModalOpen}
				sentCodeAgain={sentCodeAgain}
				error={errorVerification}
				setErrorVerification={setErrorVerification}
				validateOtpCode={validateOtpCode}
				numDigits={4}
			/>
		</>
	);
};
